import { useContent } from '@/hooks/useContent';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const getServices = (t: (key: string) => string, imgT: (key: string) => string) => [
  {
    title: t('svc1_title'),
    desc: t('svc1_desc'),
    icon: 'ri-tools-line',
    image: imgT('svc_spray_foam'),
  },
  {
    title: t('svc2_title'),
    desc: t('svc2_desc'),
    icon: 'ri-archive-line',
    image: imgT('svc_batt'),
  },
  {
    title: t('svc3_title'),
    desc: t('svc3_desc'),
    icon: 'ri-recycle-line',
    image: imgT('svc_cellulose'),
  },
  {
    title: t('svc4_title'),
    desc: t('svc4_desc'),
    icon: 'ri-home-4-line',
    image: imgT('svc_attic_walls'),
  },
  {
    title: t('svc5_title'),
    desc: t('svc5_desc'),
    icon: 'ri-building-line',
    image: imgT('svc_crawl'),
  },
  {
    title: t('svc6_title'),
    desc: t('svc6_desc'),
    icon: 'ri-building-line',
    image: imgT('svc_metal'),
  },
];

const getServiceAreas = (t: (key: string) => string) => [
  {
    name: t('area_lafayette'),
    desc: t('area_lafayette_desc'),
  },
  {
    name: t('area_west_lafayette'),
    desc: t('area_west_lafayette_desc'),
  },
  {
    name: t('area_surrounding'),
    desc: t('area_surrounding_desc'),
  },
];

export default function ServicesPage() {
  const { t } = useContent('services');
  const { t: tc } = useContent('common');
  const { t: imgT } = useContent('images');
  const services = getServices(t, imgT);
  const serviceAreas = getServiceAreas(t);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center overflow-hidden pt-12 md:pt-16">
          <div className="absolute inset-0">
            <img
              alt="FMO LLC professional insulation services"
              className="w-full h-full object-cover object-center"
              src={imgT('services_area_bg')}
            />
            <div className="absolute inset-0 bg-black/70"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center">
            <div className="bg-black/70 border-2 border-primary-500 rounded-xl px-14 py-6 md:px-20 md:py-7">
              <h1 className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-[0.2em] text-center">{t('services_hero_title')}</h1>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-primary-500 text-xs font-semibold uppercase tracking-widest">{t('services_badge')}</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight">
                {t('services_title')}
              </h2>
              <p className="text-foreground-400 mt-4 text-base max-w-2xl mx-auto">
                {t('services_desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <div key={svc.title} className="group bg-background-100 border border-white/5 hover:border-primary-500/40 rounded-2xl overflow-hidden transition-all duration-700 hover:-translate-y-1">
                  <div className="relative w-full h-56 overflow-hidden">
                    <img
                      alt={svc.title}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      src={svc.image}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500"></div>
                    <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full">
                      <i className={`${svc.icon} text-white text-base w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-bold px-6 py-3 rounded-full text-sm transition-all duration-300 cursor-pointer transform translate-y-3 group-hover:translate-y-0"
                      >
                        {tc('common_request_this_service')}
                        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                      </Link>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-foreground-50 font-bold text-base mb-2">{svc.title}</h3>
                    <p className="text-foreground-400 text-sm leading-relaxed">{svc.desc}</p>
                    <div className="mt-4 w-12 h-0.5 bg-primary-500/40 group-hover:bg-primary-500 group-hover:w-20 transition-all duration-500"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-primary-500 text-xs font-semibold uppercase tracking-widest">{t('areas_badge')}</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight">
                {t('areas_title')}
              </h2>
              <p className="text-foreground-400 mt-4 text-base max-w-2xl mx-auto">
                {t('areas_desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {serviceAreas.map((area) => (
                <div key={area.name} className="bg-background-50 border border-white/5 rounded-2xl p-8 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-500/15 rounded-2xl mx-auto mb-5">
                    <i className="ri-map-pin-line text-primary-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
                  </div>
                  <h3 className="text-foreground-50 font-bold text-lg mb-3">{area.name}</h3>
                  <p className="text-foreground-400 text-sm leading-relaxed">{area.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full py-20 md:py-28 flex items-center justify-center overflow-hidden mb-8 md:mb-12">
          <div className="absolute inset-0">
            <img
              alt="FMO LLC professional insulation contractor"
              className="w-full h-full object-cover object-center"
              src={imgT('services_cta_bg')}
            />
            <div className="absolute inset-0 bg-black/85"></div>
          </div>
          <div className="relative z-10 text-center max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight">{t('cta_title')}</h2>
            <p className="text-white/70 mt-4 text-base leading-relaxed max-w-xl mx-auto">
              {t('cta_desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer"
              >
                {tc('common_request_quote')}
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </Link>
              <a
                href="tel:+15747219810"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer"
              >
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                (574) 721-9810
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}